// ABOUTME: Deployment environment type, display table, and derivation of the environment from the request hostname.
// ABOUTME: Pure (no import.meta / DOM) so it is unit-testable under jest.

export type Environment = 'development' | 'staging' | 'production' | 'unknown';

export interface EnvironmentDisplay {
  label: string;
  /** When set, this environment shows a persistent caution banner with this text. */
  banner?: string;
}

// One presentation table keyed by Environment. Exhaustive by the Record type, so
// adding an Environment forces a display entry; the banner is opt-in per env.
export const ENVIRONMENTS: Record<Environment, EnvironmentDisplay> = {
  development: { label: 'Development' },
  staging: { label: 'Staging' },
  production: {
    label: 'Production',
    banner:
      'You are viewing Production. Changes here affect live operator data.',
  },
  unknown: {
    label: 'Unknown',
    banner:
      'Environment is not configured. Treat this as a live environment until confirmed.',
  },
};

/** Deployment host suffix per environment. staging/dev live under the non-prod (np) zone. */
const ENV_HOST_SUFFIX: Record<'production' | 'staging', string> = {
  production: 'prod.trustflight.io',
  staging: 'staging.np.trustflight.io',
};

/**
 * Derives the deployment environment from the request hostname. The environment
 * is a distinct DNS label in the deployment host, so we match on labels rather
 * than substrings. Local hosts are development; any host we don't recognize
 * resolves to `unknown` so it shows a caution banner rather than a false-safe
 * `development`.
 */
export function environmentFromHostname(hostname: string): Environment {
  const host = hostname.trim().toLowerCase();
  if (
    host === '' ||
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host === '[::1]'
  ) {
    return 'development';
  }
  const labels = host.split('.');
  if (labels.includes('prod')) return 'production';
  if (labels.includes('staging')) return 'staging';
  if (labels.includes('dev')) return 'development';
  return 'unknown';
}

/**
 * The URL of the counterpart deployment for the "Switch environment" link,
 * derived from the current location. Only production and staging have a
 * counterpart (they mirror each other); every other environment returns
 * undefined so no switch link is shown.
 */
export function altEnvUrlFromLocation(
  protocol: string,
  hostname: string,
): string | undefined {
  const env = environmentFromHostname(hostname);
  // A host classified by DNS label but missing the expected suffix rewrites to
  // itself; a switch link back to the current environment is worse than none.
  if (env === 'production') {
    const alt = hostname.replace(
      ENV_HOST_SUFFIX.production,
      ENV_HOST_SUFFIX.staging,
    );
    return alt === hostname ? undefined : `${protocol}//${alt}`;
  }
  if (env === 'staging') {
    const alt = hostname.replace(
      ENV_HOST_SUFFIX.staging,
      ENV_HOST_SUFFIX.production,
    );
    return alt === hostname ? undefined : `${protocol}//${alt}`;
  }
  return undefined;
}
